import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  SupportAgent as SupportAgentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  HourglassEmpty as HourglassIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  WhatsApp as WhatsAppIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { AtendimentoTicket, atendimentoService } from '../../../services/atendimentoService';
import ClientSelect from '../../../components/selects/ClientSelect';
import { clientService } from '../../../services/clientService';
import invoiceService from '../../../services/invoiceService';
import contractService from '../../../services/contractService';
import tagService from '../../../services/tagService';
import { Client, Contract, Invoice, Tag } from '../../../types';
import { getCountryByNumber } from '../../../utils/countries';
import InvoiceViewModal from '../../../components/modals/InvoiceViewModal';
import ContactForm from '../../../components/forms/ContactForm';
import PreCadastroForm from '../../../components/forms/PreCadastroForm';
import { formatarData } from '../../../utils/formatToView';


// ── Helper ───────────────────────────────────────────────────────────────────
const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

const formatCurrency = (value?: number | string | null) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const normalizeHexColor = (color?: string | null) => {
  if (!color) return '#1565c0';
  return color.startsWith('#') ? color : `#${color}`;
};

const getInvoiceStatusLabel = (status: Invoice['status']) => {
  switch (status) {
    case 'pago':
      return 'paga';
    case 'vencido':
      return 'vencida';
    case 'aberto':
      return 'em aberto';
    case 'parcialmente_pago':
      return 'parcial';
    case 'cancelado':
      return 'cancelada';
    case 'premiado':
      return 'premiada';
    default:
      return status;
  }
};

const getInvoiceStatusColor = (status: Invoice['status']) => {
  switch (status) {
    case 'pago':
      return 'success';
    case 'vencido':
      return 'error';
    case 'parcialmente_pago':
      return 'warning';
    case 'cancelado':
      return 'default';
    case 'premiado':
      return 'secondary';
    default:
      return 'info';
  }
};

const formatStatusLabel = (status?: string | null) =>
  status ? status.replace(/_/g, ' ') : '-';

const formatConnectionType = (tipo?: string) => {
  if (tipo === 'whatsapp') return 'WhatsApp';
  if (tipo === 'instagram') return 'Instagram';
  return tipo || 'Conexao';
};

const getClientStatusColor = (status?: Client['status']) => {
  switch (status) {
    case 'ativo':
      return 'success';
    case 'inadimplente':
    case 'bloqueado':
      return 'error';
    case 'suspenso':
      return 'warning';
    default:
      return 'default';
  }
};

const getContractStatusColor = (status?: string) => {
  switch (status) {
    case 'ativo':
      return 'success';
    case 'bloqueado':
    case 'cancelado':
      return 'error';
    case 'suspenso':
    case 'aguardando_instalacao':
    case 'aguardando_assinatura':
      return 'warning';
    default:
      return 'default';
  }
};

// ── Seção colapsável ─────────────────────────────────────────────────────────
interface SecaoProps {
  titulo: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  headerAction?: React.ReactNode;
}

const Secao: React.FC<SecaoProps> = ({ titulo, children, defaultOpen = true, headerAction }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', px: 2, py: 1, '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {titulo}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.25} onClick={(e) => e.stopPropagation()}>
          {headerAction}
          {open
            ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          }
        </Box>
      </Box>
      <Collapse in={open}>{children}</Collapse>
      <Divider />
    </Box>
  );
};

// ── Props ────────────────────────────────────────────────────────────────────
export interface ContatoDetalhesProps {
  atendimento: AtendimentoTicket;
  onUpdate?: (atendimento: AtendimentoTicket) => void;
  onClose?: () => void;
  fullWidth?: boolean;
  onOpenClientProfile?: (clientId: number, clientName?: string) => void;
}

// ── Componente ───────────────────────────────────────────────────────────────
const ContatoDetalhes: React.FC<ContatoDetalhesProps> = ({
  atendimento,
  onUpdate,
  onClose,
  fullWidth = false,
  onOpenClientProfile,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [modalVinculoAberto, setModalVinculoAberto] = useState(false);
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState<number | null>(null);
  const [clienteSelecionado, setClienteSelecionado] = useState<Client | null>(null);
  const [clienteDetalhes, setClienteDetalhes] = useState<Client | null>(null);
  const [faturasCliente, setFaturasCliente] = useState<Invoice[]>([]);
  const [contratosCliente, setContratosCliente] = useState<Contract[]>([]);
  const [loadingDadosCliente, setLoadingDadosCliente] = useState(false);
  const [loadingFaturas, setLoadingFaturas] = useState(false);
  const [erroCliente, setErroCliente] = useState<string | null>(null);
  const [erroFaturas, setErroFaturas] = useState<string | null>(null);
  const [contratosAberto, setContratosAberto] = useState(false);
  const [salvandoVinculo, setSalvandoVinculo] = useState(false);
  const [desvinculandoCliente, setDesvinculandoCliente] = useState(false);
  const [erroVinculo, setErroVinculo] = useState<string | null>(null);
  const [modalFotoAberto, setModalFotoAberto] = useState(false);
  const [invoiceViewId, setInvoiceViewId] = useState<number | null>(null);
  const [modalEditarContatoAberto, setModalEditarContatoAberto] = useState(false);
  const [modalPreCadastroAberto, setModalPreCadastroAberto] = useState(false);
  const [contatoEditando, setContatoEditando] = useState<any>(null);
  const [salvandoContato, setSalvandoContato] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    tagService.getAll({ limit: 1000 }).then((res) => {
      if (res.success && res.data) {
        setAllTags(res.data);
      }
    }).catch(() => { });
  }, []);

  const contactTags = React.useMemo(() => {
    const rawTags = atendimento.contato?.tags;
    if (!rawTags) return [];
    let rawArray: any[] = [];
    if (Array.isArray(rawTags)) {
      rawArray = rawTags;
    } else if (typeof rawTags === 'string') {
      try {
        const parsed = JSON.parse(rawTags);
        if (Array.isArray(parsed)) rawArray = parsed;
      } catch (e) {
        rawArray = [];
      }
    }
    const ids = rawArray.map((v) => typeof v === 'object' && v !== null ? Number(v.id) : Number(v)).filter((n) => !isNaN(n));
    return allTags.filter((t) => ids.includes(t.id));
  }, [atendimento.contato?.tags, allTags]);

  const clienteId = atendimento.contato.cliente_id || atendimento.cliente_id || null;
  const temContratoBloqueado = contratosCliente.some((contrato) => contrato.status === 'bloqueado');
  const corSetor = normalizeHexColor(atendimento.setor?.cor);
  const dadosCliente: Array<[string, string | number | null | undefined]> = clienteDetalhes
    ? [
      ['Nome', clienteDetalhes.nome],
      ['Status', formatStatusLabel(clienteDetalhes.status)],
      ['Cliente desde', formatarData(clienteDetalhes.createdAt)],
      ...(clienteDetalhes.motivo_desativacao && clienteDetalhes.motivo_desativacao.trim() !== '' ? [['Motivo Desativação', clienteDetalhes.motivo_desativacao] as [string, string | undefined]] : []),
      ['CPF/CNPJ', clienteDetalhes.cpf_cnpj],
      ['Cidade', clienteDetalhes.cidade + '-' + clienteDetalhes.estado],
      ['Endereço', clienteDetalhes.endereco + ', ' + clienteDetalhes.numero],
      ['Bairro', clienteDetalhes.bairro],
      ['Complemento', clienteDetalhes.complemento],
      ...(clienteDetalhes.observacoes && clienteDetalhes.observacoes.trim() !== '' ? [['Observações', clienteDetalhes.observacoes] as [string, string | undefined]] : []),
    ]
    : [];

  const carregarDadosCliente = useCallback(async () => {
    if (!clienteId) {
      setClienteDetalhes(null);
      setContratosCliente([]);
      setErroCliente(null);
      return;
    }

    try {
      setLoadingDadosCliente(true);
      setErroCliente(null);
      const [cliente, contratosResp] = await Promise.all([
        clientService.getById(clienteId),
        contractService.getByClientId(clienteId, { limit: 100 }),
      ]);
      setClienteDetalhes(cliente);
      setContratosCliente(contratosResp.data || []);
    } catch (err: any) {
      setClienteDetalhes(null);
      setContratosCliente([]);
      setErroCliente(err?.response?.data?.message || err?.message || 'Erro ao carregar dados do cliente.');
    } finally {
      setLoadingDadosCliente(false);
    }
  }, [clienteId]);

  const carregarFaturasCliente = useCallback(async () => {
    if (!clienteId) {
      setFaturasCliente([]);
      setErroFaturas(null);
      return;
    }

    try {
      setLoadingFaturas(true);
      setErroFaturas(null);
      const faturasResp = await invoiceService.getInvoicesByClient(clienteId);
      setFaturasCliente(faturasResp.data || []);
    } catch (err: any) {
      setFaturasCliente([]);
      setErroFaturas(err?.response?.data?.message || err?.message || 'Erro ao carregar faturas.');
    } finally {
      setLoadingFaturas(false);
    }
  }, [clienteId]);

  useEffect(() => {
    carregarDadosCliente();
    carregarFaturasCliente();
  }, [carregarDadosCliente, carregarFaturasCliente]);

  useEffect(() => {
    setContratosAberto(false);
  }, [clienteId]);

  useEffect(() => {
    if (temContratoBloqueado) {
      setContratosAberto(true);
    }
  }, [temContratoBloqueado]);

  const abrirModalVinculo = () => {
    setClienteSelecionadoId(null);
    setClienteSelecionado(null);
    setErroVinculo(null);
    setModalVinculoAberto(true);
  };

  const confirmarVinculo = async () => {
    if (!clienteSelecionadoId) {
      setErroVinculo('Selecione um cliente para vincular.');
      return;
    }

    try {
      setSalvandoVinculo(true);
      setErroVinculo(null);
      const contatoAtualizado = await atendimentoService.updateContato(atendimento.contato.id, {
        cliente_id: clienteSelecionadoId,
      });

      onUpdate?.({
        ...atendimento,
        cliente_id: clienteSelecionadoId,
        contato: {
          ...atendimento.contato,
          ...contatoAtualizado,
          cliente_id: clienteSelecionadoId,
          cliente: clienteSelecionado
            ? {
              id: clienteSelecionado.id,
              nome: clienteSelecionado.nome,
              razao_social: (clienteSelecionado as any).razao_social,
            }
            : atendimento.contato.cliente,
        },
      });

      setModalVinculoAberto(false);
    } catch (err: any) {
      setErroVinculo(err?.response?.data?.error || err?.message || 'Erro ao vincular cliente.');
    } finally {
      setSalvandoVinculo(false);
    }
  };

  const abrirModalEditarContato = () => {
    setContatoEditando({
      ...atendimento.contato,
      tipo: atendimento.contato.numero ? 'celular' : 'email',
      is_whatsapp: atendimento.contato.canal === 'whatsapp',
      recebe_notificacao: true,
      is_default: false,
      bot_desativado: atendimento.contato.bot_desativado || false,
      tags: atendimento.contato.tags || [],
    });
    setModalEditarContatoAberto(true);
  };

  const handleSalvarContato = async (data: any) => {
    setSalvandoContato(true);
    try {
      const payload = {
        nome: data.nome,
        tipo: data.tipo,
        numero: data.numero,
        ddi: data.ddi,
        is_whatsapp: data.is_whatsapp,
        cliente_id: data.cliente_id,
        is_default: data.is_default,
        recebe_notificacao: data.recebe_notificacao,
        email: data.email,
        bot_desativado: data.bot_desativado,
        tags: data.tags,
      };
      const contatoAtualizado = await atendimentoService.updateContato(atendimento.contato.id, payload);

      onUpdate?.({
        ...atendimento,
        cliente_id: contatoAtualizado.cliente_id ?? atendimento.cliente_id,
        contato: {
          ...atendimento.contato,
          ...contatoAtualizado,
        },
      });

      setModalEditarContatoAberto(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro ao salvar contato.');
    } finally {
      setSalvandoContato(false);
    }
  };

  const desvincularCliente = async () => {
    if (!clienteId) return;

    try {
      setDesvinculandoCliente(true);
      setErroCliente(null);
      const contatoAtualizado = await atendimentoService.updateContato(atendimento.contato.id, {
        cliente_id: null,
      });

      setClienteDetalhes(null);
      setFaturasCliente([]);
      setContratosCliente([]);
      setContratosAberto(false);
      setErroCliente(null);
      setErroFaturas(null);

      onUpdate?.({
        ...atendimento,
        cliente_id: null,
        contato: {
          ...atendimento.contato,
          ...contatoAtualizado,
          cliente_id: null,
          cliente: undefined,
        },
      });
    } catch (err: any) {
      setErroCliente(err?.response?.data?.error || err?.message || 'Erro ao desvincular cliente.');
    } finally {
      setDesvinculandoCliente(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: fullWidth ? '100%' : 280,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderLeft: fullWidth ? 'none' : `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 2 },
        }}
      >
        {onClose && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, pt: 0.5 }}>
            <Tooltip title="Fechar detalhes">
              <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* ── Perfil ──────────────────────────────────────────────────────────────── */}
        <Box sx={{ px: 2, pt: onClose ? 0.5 : 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Avatar
              src={atendimentoService.getAvatarUrl(atendimento.contato.foto_perfil)}
              onClick={() => {
                if (atendimento.contato.foto_perfil) {
                  setModalFotoAberto(true);
                }
              }}
              sx={{
                bgcolor: '#26a69a',
                width: 60, height: 60,
                fontSize: '1.2rem',
                boxShadow: 2,
                cursor: atendimento.contato.foto_perfil ? 'pointer' : 'default',
              }}
            >
              {!atendimento.contato.foto_perfil && getInitials(atendimento.contato.nome)}
            </Avatar>

            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Tooltip title="Editar contato">
                  <IconButton
                    size="small"
                    onClick={abrirModalEditarContato}
                    sx={{ p: 0.25, flexShrink: 0, color: 'text.secondary' }}
                  >
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Typography variant="subtitle2" fontWeight={700} noWrap title={atendimento.contato.nome} sx={{ minWidth: 0 }}>
                  {atendimento.contato.nome}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                {(() => {
                  const country = getCountryByNumber(atendimento.contato.ddi || atendimento.contato.numero);
                  return country ? (
                    <Tooltip title={country.label}>
                      <img
                        loading="lazy"
                        width="14"
                        src={new URL(`../../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                        alt={country.label}
                        style={{ borderRadius: 2 }}
                      />
                    </Tooltip>
                  ) : null;
                })()}
                <Typography variant="caption" color="text.secondary" noWrap>
                  {atendimento.contato.numero}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: atendimento.contato.bot_desativado ? 'error.main' : 'success.main',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  mt: 0.2,
                }}
              >
                {atendimento.contato.bot_desativado ? <SmartToyIcon color='error' sx={{ fontSize: '1rem', mr: 0.5, mb: 0.2 }} /> : <SmartToyIcon color='success' sx={{ fontSize: '1rem', mr: 0.5, mb: 0.2 }} />}{atendimento.contato.bot_desativado ? 'Bot Desativado' : 'Bot Ativado'}
              </Typography>
            </Box>
          </Box>

          {contactTags.length > 0 && (
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={1} mb={1}>
              {contactTags.map((t) => (
                <Chip
                  key={t.id}
                  label={t.titulo}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    bgcolor: alpha(t.color, 0.15),
                    color: t.color,
                    border: `1px solid ${alpha(t.color, 0.3)}`,
                  }}
                />
              ))}
            </Box>
          )}

          {clienteId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mt: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onClick={() => onOpenClientProfile?.(
                  clienteId,
                  clienteDetalhes?.nome || atendimento.contato.cliente?.nome,
                )}
                sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 2 }}
              >
                Ver perfil do cliente
              </Button>
              <Button
                fullWidth
                variant="text"
                color="error"
                size="small"
                onClick={desvincularCliente}
                disabled={desvinculandoCliente}
                startIcon={desvinculandoCliente ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 2 }}
              >
                Desvincular cliente
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mt: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={abrirModalVinculo}
                sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 2 }}
              >
                Vincular cliente
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                onClick={() => setModalPreCadastroAberto(true)}
                sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 2 }}
              >
                Pré cadastro
              </Button>
            </Box>
          )}
        </Box>
        <Divider />

        {/* ── Detalhes do Ticket ──────────────────────────────────────────────────────────── */}
        <Secao titulo="Informações do atendimento">
          <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="text.secondary">Atendimento</Typography>
              <Typography variant="caption" fontWeight={700} color="text.primary">
                #{atendimento.id}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="text.secondary">Setor</Typography>
              {atendimento.setor ? (
                <Chip
                  label={atendimento.setor.nome}
                  size="small"
                  sx={{
                    height: 20,
                    maxWidth: 140,
                    bgcolor: corSetor,
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    '& .MuiChip-label': {
                      px: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ) : (
                <Typography variant="caption" color="text.disabled">Sem setor</Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Box display="flex" alignItems="center" gap={0.8} minWidth={0}>
                <WhatsAppIcon sx={{ fontSize: 16, color: '#25d366', flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">Conexão</Typography>
              </Box>
              {atendimento.conexao ? (
                <Box display="flex" flexDirection="column" alignItems="flex-end" minWidth={0}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    noWrap
                    title={atendimento.conexao.nome}
                    sx={{ maxWidth: 150 }}
                  >
                    {atendimento.conexao.nome}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={[formatConnectionType(atendimento.conexao.tipo), atendimento.conexao.numero].filter(Boolean).join(' - ')}
                    sx={{ maxWidth: 150, fontSize: '0.65rem' }}
                  >
                    {[formatConnectionType(atendimento.conexao.tipo), atendimento.conexao.numero].filter(Boolean).join(' - ')}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled">Sem conexão</Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Box display="flex" alignItems="center" gap={0.8}>
                <SupportAgentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Atendente</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                {atendimento.usuario ? (
                  <>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.5rem', bgcolor: '#5c6bc0' }}>
                      {getInitials(atendimento.usuario.nome)}
                    </Avatar>
                    <Typography variant="caption" fontWeight={600}>
                      {atendimento.usuario.nome}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.disabled">Não atribuído</Typography>
                )}
                <ArrowDownIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
            </Box>
          </Box>
        </Secao>

        {/* ── Dados do cliente ────────────────────────────────────────────────────── */}
        <Secao
          titulo="Dados do cliente"
          defaultOpen
          headerAction={clienteId ? (
            <Tooltip title="Atualizar">
              <span>
                <IconButton
                  size="small"
                  onClick={carregarDadosCliente}
                  disabled={loadingDadosCliente}
                  sx={{ p: 0.35, color: 'text.secondary' }}
                >
                  {loadingDadosCliente
                    ? <CircularProgress size={14} />
                    : <RefreshIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : undefined}
        >
          <Box sx={{ px: 2, py: 1 }}>
            {!clienteId ? (
              <Typography variant="caption" color="text.disabled">
                Nenhum cliente vinculado.
              </Typography>
            ) : loadingDadosCliente && !clienteDetalhes ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Carregando dados...
                </Typography>
              </Box>
            ) : erroCliente ? (
              <Alert severity="error" sx={{ fontSize: '0.72rem', py: 0 }}>
                {erroCliente}
              </Alert>
            ) : (
              <>
                {dadosCliente.map(([key, val]) => (
                  <Box
                    key={key}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                    py={0.4}
                    sx={{
                      borderBottom: `1px dashed ${theme.palette.divider}`,
                      '&:last-child': { border: 'none' },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                      {key}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      textAlign="right"
                      sx={{ fontSize: '0.7rem', color: 'text.primary', wordBreak: 'break-word' }}
                    >
                      {key === 'Status' && clienteDetalhes?.status ? (
                        <Chip
                          label={formatStatusLabel(clienteDetalhes.status)}
                          size="small"
                          color={getClientStatusColor(clienteDetalhes.status) as any}
                          sx={{ height: 18, fontSize: '0.62rem', textTransform: 'capitalize' }}
                        />
                      ) : (
                        val || '-'
                      )}
                    </Typography>
                  </Box>
                ))}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  py={0.4}
                  sx={{ borderBottom: contratosAberto ? `1px dashed ${theme.palette.divider}` : 'none' }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                      Contratos
                    </Typography>
                    {temContratoBloqueado && (
                      <Tooltip title="Existe contrato bloqueado">
                        <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setContratosAberto((v) => !v)}
                    endIcon={contratosAberto ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                    sx={{ minWidth: 0, p: 0, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none' }}
                  >
                    {contratosCliente.length}
                  </Button>
                </Box>
                <Collapse in={contratosAberto}>
                  <Box sx={{ pt: 0.5 }}>
                    {contratosCliente.length === 0 ? (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                        Nenhum contrato encontrado.
                      </Typography>
                    ) : (
                      contratosCliente.map((contrato) => (
                        <Box
                          key={contrato.id}
                          onClick={() => navigate(`/contracts/${contrato.id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 0.55,
                            cursor: 'pointer',
                            borderBottom: `1px dashed ${theme.palette.divider}`,
                            '&:last-child': { border: 'none' },
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" fontWeight={600} noWrap sx={{ fontSize: '0.7rem', display: 'block' }}>
                              {contrato.titulo || `Contrato #${contrato.id}`}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                              {formatarData(contrato.data_contrato)}
                            </Typography>
                          </Box>
                          <Chip
                            label={formatStatusLabel(contrato.status)}
                            size="small"
                            color={getContractStatusColor(contrato.status) as any}
                            sx={{ height: 18, fontSize: '0.6rem', textTransform: 'capitalize', flexShrink: 0 }}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        </Secao>

        {/* ── Faturas do cliente ──────────────────────────────────────────────────── */}
        <Secao
          titulo="Faturas do cliente"
          defaultOpen={false}
          headerAction={clienteId ? (
            <Tooltip title="Atualizar">
              <span>
                <IconButton
                  size="small"
                  onClick={carregarFaturasCliente}
                  disabled={loadingFaturas}
                  sx={{ p: 0.35, color: 'text.secondary' }}
                >
                  {loadingFaturas
                    ? <CircularProgress size={14} />
                    : <RefreshIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : undefined}
        >
          <Box sx={{ px: 2, py: 1 }}>
            {!clienteId ? (
              <Typography variant="caption" color="text.disabled">
                Vincule um cliente para visualizar faturas.
              </Typography>
            ) : loadingFaturas && faturasCliente.length === 0 ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Carregando faturas...
                </Typography>
              </Box>
            ) : erroFaturas ? (
              <Alert severity="error" sx={{ fontSize: '0.72rem', py: 0 }}>
                {erroFaturas}
              </Alert>
            ) : faturasCliente.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                Nenhuma fatura encontrada.
              </Typography>
            ) : (
              faturasCliente.map((fat) => {
                const statusIcon =
                  fat.status === 'pago' ? <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} /> :
                    fat.status === 'vencido' ? <WarningIcon sx={{ fontSize: 13, color: 'error.main' }} /> :
                      fat.status === 'parcialmente_pago' ? <HourglassIcon sx={{ fontSize: 13, color: 'warning.main' }} /> :
                        <ReceiptIcon sx={{ fontSize: 13, color: 'info.main' }} />;

                const statusColor = getInvoiceStatusColor(fat.status);
                const statusLabel = getInvoiceStatusLabel(fat.status);

                return (
                  <Box
                    key={fat.id}
                    onClick={() => setInvoiceViewId(fat.id)}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={0.6}
                    sx={{
                      borderBottom: `1px dashed ${theme.palette.divider}`,
                      cursor: 'pointer',
                      borderRadius: 1,
                      px: 0.5,
                      mx: -0.5,
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                      '&:last-child': { border: 'none' },
                      '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'primary.main',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.6}>
                      {statusIcon}
                      <Box>
                        <Typography variant="caption" fontWeight={600} display="block" sx={{ fontSize: '0.72rem', lineHeight: 1.2 }}>
                          {fat.id || fat.descricao || `Fatura #${fat.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                          Venc. {formatarData(fat.data_vencimento)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3}>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem' }}>
                        {formatCurrency(fat.valor)}
                      </Typography>
                      <Chip
                        label={statusLabel}
                        size="small"
                        color={statusColor as any}
                        sx={{ height: 16, fontSize: '0.58rem', textTransform: 'capitalize' }}
                      />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Secao>
      </Box>

      <Dialog
        open={modalVinculoAberto}
        onClose={() => !salvandoVinculo && setModalVinculoAberto(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>Vincular cliente</DialogTitle>
        <DialogContent>
          {erroVinculo && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erroVinculo}
            </Alert>
          )}

          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5, mt: 1 }}>
            <ClientSelect
              value={clienteSelecionadoId}
              onChange={(clientId, client) => {
                setClienteSelecionadoId(clientId);
                setClienteSelecionado(client || null);
                setErroVinculo(null);
              }}
              label="Cliente"
              placeholder="Busque pelo nome ou CPF/CNPJ"
              required
              disabled={salvandoVinculo}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalVinculoAberto(false)}
            disabled={salvandoVinculo}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarVinculo}
            disabled={!clienteSelecionadoId || salvandoVinculo}
            startIcon={salvandoVinculo ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalFotoAberto}
        onClose={() => setModalFotoAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <IconButton
            onClick={() => setModalFotoAberto(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {atendimento.contato.foto_perfil && (
            <img
              src={atendimentoService.getAvatarUrl(atendimento.contato.foto_perfil)}
              alt="Foto do Contato"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {invoiceViewId && (
        <InvoiceViewModal
          open={Boolean(invoiceViewId)}
          invoice_id={invoiceViewId}
          onClose={() => setInvoiceViewId(null)}
        />
      )}

      <ContactForm
        open={modalEditarContatoAberto}
        onClose={() => setModalEditarContatoAberto(false)}
        onSubmit={handleSalvarContato}
        editingContact={contatoEditando}
        clientId={contatoEditando?.cliente_id || atendimento.contato.cliente_id || undefined}
        loading={salvandoContato}
      />

      <PreCadastroForm
        open={modalPreCadastroAberto}
        onClose={() => setModalPreCadastroAberto(false)}
        ticketId={atendimento.id}
        onSuccess={() => {
          setModalPreCadastroAberto(false);
        }}
      />
    </>
  );
};

export default ContatoDetalhes;
